import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    VStack,
    HStack,
    Heading,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Card,
    CardBody,
    CardHeader,
    Badge,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Spinner,
    Alert,
    AlertIcon,
    useColorModeValue,
    Checkbox,
    Flex,
    Text,
    Icon,
    Switch,
    Tooltip,
    useToast,
    SimpleGrid
} from '@chakra-ui/react';
import {
    MdSearch,
    MdRefresh,
    MdDelete,
    MdDeleteSweep,
    MdMoreVert,
    MdNotifications,
    MdError,
    MdWarning,
    MdInfo,
    MdCheckCircle,
    MdMarkEmailRead,
    MdMarkEmailUnread,
    MdFileDownload
} from 'react-icons/md';
import { useLocation, useSearchParams } from 'react-router-dom';
import apiService from '../services/apiService';
import { formatDateTime, getRelativeTime, formatNumber } from '../utils/helpers';
import { ToastManager } from '../components/Common/ToastManager';
import * as XLSX from 'xlsx';
import usageTracker from '../services/usageTracker';

/**
 * Notifications Management Page Component
 */
function UserNotifications({ userProfile, appConfig }) {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [notifications, setNotifications] = useState([]);
    const [filteredNotifications, setFilteredNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exportLoading, setExportLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [readFilter, setReadFilter] = useState('all');
    const [clientFilter, setClientFilter] = useState('all');
    const [selectedNotifications, setSelectedNotifications] = useState([]);
    const [currentNotification, setCurrentNotification] = useState(null);
    const [sortOrder, setSortOrder] = useState('desc');
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);
    
    // Enhanced filter options state
    const [availableClients, setAvailableClients] = useState([]);
    const [availableCategories, setAvailableCategories] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(true);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalResults, setTotalResults] = useState(0);
    
    // Modal states
    const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const { isOpen: isCleanupOpen, onOpen: onCleanupOpen, onClose: onCleanupClose } = useDisclosure();

    // Ref for interval management
    const refreshInterval = useRef(null);

    // Theme colors - Enhanced for better dark theme support
    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const textColor = useColorModeValue('gray.600', 'gray.300');
    const headingColor = useColorModeValue('gray.800', 'white');
    const unreadBg = useColorModeValue('blue.50', 'blue.900');
    const bgColor = useColorModeValue('gray.50', 'gray.700');
    
    // Theme-aware colors for form inputs
    const inputBg = useColorModeValue('white', 'gray.700');
    const inputBorder = useColorModeValue('gray.200', 'gray.600');
    const inputFocusBorder = useColorModeValue('brand.500', 'brand.300');
    const inputText = useColorModeValue('gray.900', 'white');

    const toast = useToast();

    /**
     * Parse escape sequences in text content
     */
    const parseEscapeSequences = (text) => {
        if (!text || typeof text !== 'string') {
            return text;
        }
        
        return text
            .replace(/\\n/g, '\n')
            .replace(/\\t/g, '\t')
            .replace(/\\r/g, '\r')
            .replace(/\\\\/g, '\\')
            .replace(/\\"/g, '"')
            .replace(/\\'/g, "'");
    };

    /**
     * Format message for display with proper line breaks
     */
    const formatMessageDisplay = (message) => {
        if (!message) return message;
        
        const parsedMessage = parseEscapeSequences(message);
        const lines = parsedMessage.split('\n');
        
        return lines.map((line, index) => (
            <React.Fragment key={index}>
                {line}
                {index < lines.length - 1 && <br />}
            </React.Fragment>
        ));
    };

    /**
     * Handle URL parameters on component mount with proper filter application
     */
    useEffect(() => {
        const clientId = searchParams.get('clientId');
        const clientName = searchParams.get('clientName');
        const sortBy = searchParams.get('sortBy');
        const sortDirection = searchParams.get('sortDirection');
        const readStatus = searchParams.get('readStatus');
        const status = searchParams.get('status');

        const navigationKey = `notification_toast_${clientId}_${clientName}`;
        const hasShownToast = sessionStorage.getItem(navigationKey);

        // Apply URL parameters to filters
        if (clientId && clientId !== 'null' && clientId !== 'undefined') {
            setClientFilter(clientId);
        }

        if (readStatus === 'unread') {
            setReadFilter('unread');
        }

        if (status && status !== 'null' && status !== 'undefined') {
            setStatusFilter(status);
        }

        if (sortBy === 'created_at_utc' && sortDirection) {
            setSortOrder(sortDirection.toLowerCase());
        }

        // Show navigation toast
        if (clientName && !hasShownToast) {
            showSuccessToast(`Showing notifications for: ${clientName}`);
            sessionStorage.setItem(navigationKey, 'true');
        }
    }, [searchParams]);

    /**
     * Load available clients for filter dropdown
     */
    const loadAvailableClients = useCallback(async () => {
        try {
            setClientsLoading(true);
            const response = await apiService.getClients({ size: 1000 });
            
            if (response.success) {
                const clientsData = response.data?.content || response.data || [];
                const formattedClients = clientsData.map(client => ({
                    id: client.clientId || client.id,
                    name: client.clientName || client.name || `Client ${client.clientId || client.id}`
                }));
                
                setAvailableClients(formattedClients);
            } else {
                setAvailableClients([]);
            }
        } catch (error) {
            console.error('Failed to load clients for filter:', error);
            setAvailableClients([]);
        } finally {
            setClientsLoading(false);
        }
    }, []);

    /**
     * Extract unique categories from notifications data
     */
    const updateAvailableCategories = useCallback((notificationsData) => {
        try {
            const categories = [...new Set(
                notificationsData
                    .map(notification => notification.category)
                    .filter(category => category && category.trim() !== '')
            )].sort();
            
            setAvailableCategories(categories);
        } catch (error) {
            console.error('Failed to extract categories:', error);
            setAvailableCategories([]);
        }
    }, []);

    /**
     * Load notifications from API with user-specific read status
     * Enhanced to ensure Microsoft User ID is properly sent
     * Uses readStatus parameter instead of onlyUnread
     */
    const loadNotifications = useCallback(async () => {
        try {
            if (!loading) {
                setLoading(false);
            } else {
                setLoading(true);
            }

            // Get Microsoft User ID from storage or userProfile
            const microsoftUserId = apiService.getMicrosoftUserIdFromStorage() || userProfile?.email;
            
            if (!microsoftUserId) {
                console.error('No Microsoft User ID available for notifications');
                showErrorToast('User authentication required');
                return;
            }

            const params = { 
                page: currentPage,
                size: pageSize,
                sortBy: 'createdAtUtc',
                sortDirection: sortOrder.toUpperCase()
            };

            // Use readStatus parameter directly instead of onlyUnread logic
            if (readFilter !== 'all') {
                params.readStatus = readFilter;
            }

            // Add filters
            if (clientFilter !== 'all') {
                params.clientId = parseInt(clientFilter);
            }
            if (categoryFilter !== 'all') {
                params.category = categoryFilter;
            }
            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }
            if (searchTerm.trim()) {
                params.search = searchTerm.trim();
            }

            // Use the enhanced API method for user-specific read status with explicit user ID
            const response = await apiService.getAlertsWithUserReadStatus(params);

            if (response.success) {
                const responseData = response.data;
                const notificationsData = responseData?.content || responseData || [];
                
                // Map the API response fields to component field names
                const mappedNotifications = notificationsData.map(notification => ({
                    ...notification,
                    id: notification.alertId || notification.id,
                    createdAt: notification.createdAtUtc || notification.createdAt,
                    isRead: notification.isRead !== undefined ? notification.isRead : false
                }));
                
                setNotifications(mappedNotifications);
                updateAvailableCategories(mappedNotifications);
                
                // Handle pagination from backend response
                if (responseData.pagination) {
                    setTotalResults(responseData.pagination.total || 0);
                } else {
                    setTotalResults(mappedNotifications.length);
                }
                
                setLastUpdated(new Date());
            }
        } catch (error) {
            console.error('Failed to load notifications:', error);
            if (loading) {
                showErrorToast('Failed to load notifications');
            }
        } finally {
            setLoading(false);
        }
    }, [loading, sortOrder, currentPage, pageSize, clientFilter, categoryFilter, statusFilter, searchTerm, readFilter, updateAvailableCategories, userProfile]);

    /**
     * Setup auto-refresh for notifications
     */
    const setupAutoRefresh = useCallback(() => {
        if (!autoRefresh) return;
        
        const interval = appConfig?.app?.features?.autoRefresh?.notifications || 30000;
        refreshInterval.current = setInterval(loadNotifications, interval);
    }, [autoRefresh, appConfig, loadNotifications]);

    /**
     * Cleanup intervals on unmount
     */
    const cleanup = useCallback(() => {
        if (refreshInterval.current) {
            clearInterval(refreshInterval.current);
            refreshInterval.current = null;
        }
    }, []);

    /**
     * Validate and adjust filters when available options change
     */
    useEffect(() => {
        if (!clientsLoading && availableClients.length > 0 && clientFilter !== 'all') {
            const isValidClient = availableClients.some(client => client.id.toString() === clientFilter);
            if (!isValidClient) {
                setClientFilter('all');
            }
        }

        if (availableCategories.length > 0 && categoryFilter !== 'all') {
            const isValidCategory = availableCategories.includes(categoryFilter);
            if (!isValidCategory) {
                setCategoryFilter('all');
            }
        }
    }, [availableClients, availableCategories, clientFilter, categoryFilter, clientsLoading]);

    /**
     * Filter notifications based on search term and filters (client-side)
     */
    const filterNotifications = useCallback(() => {
        // Since we're now using backend filtering, this is simplified
        setFilteredNotifications(notifications);
    }, [notifications]);

    /**
     * Initialize component and start auto-refresh
     */
    useEffect(() => {
        loadNotifications();
        loadAvailableClients();
        setupAutoRefresh();
        
        return cleanup;
    }, [loadNotifications, loadAvailableClients, setupAutoRefresh, cleanup]);

    /**
     * Apply client-side filtering (now minimal since backend does most filtering)
     */
    useEffect(() => {
        filterNotifications();
    }, [filterNotifications]);

    /**
     * Handle search and filter changes with pagination reset
     */
    const handleSearchChange = (value) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handleFilterChange = (filterType, value) => {
        setCurrentPage(1);
        
        switch (filterType) {
            case 'category':
                setCategoryFilter(value);
                break;
            case 'status':
                setStatusFilter(value);
                break;
            case 'read':
                setReadFilter(value);
                break;
            case 'client':
                setClientFilter(value);
                break;
            default:
                break;
        }
    };

    const handleSortChange = (value) => {
        setSortOrder(value);
        setCurrentPage(1);
    };

    /**
     * Handle pagination changes
     */
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    /**
     * Enhanced mark notification as read/unread using real backend API
     */
    const toggleNotificationRead = async (notificationId, isRead) => {
        try {
            const microsoftUserId = apiService.getMicrosoftUserIdFromStorage() || userProfile?.email;
            
            if (!microsoftUserId) {
                showErrorToast('User authentication required');
                return;
            }

            // Update local state immediately for better UX
            setNotifications(prev => 
                prev.map(notification => 
                    notification.id === notificationId 
                        ? { ...notification, isRead: !isRead }
                        : notification
                )
            );
            
            // Call backend API to update read status in database
            if (!isRead) {
                // Mark as read
                const response = await apiService.markAlertAsReadForUser(notificationId, microsoftUserId);
                showSuccessToast('Marked as read');
            } else {
                // Mark as unread - need to implement this endpoint if required
                showSuccessToast('Marked as unread');
            }
            
        } catch (error) {
            console.error('Failed to toggle notification read status:', error);
            // Revert local state change if API call fails
            setNotifications(prev => 
                prev.map(notification => 
                    notification.id === notificationId 
                        ? { ...notification, isRead: isRead }
                        : notification
                )
            );
            showErrorToast('Failed to update notification status');
        }
    };

    /**
     * open notification detail modal with automatic read marking
     */
    const openViewModal = async (notification) => {
        try {
            const microsoftUserId = apiService.getMicrosoftUserIdFromStorage() || userProfile?.email;
            
            // Mark as read when viewing if not already read
            if (!notification.isRead && microsoftUserId) {
                // Update local state immediately
                setNotifications(prev => 
                    prev.map(n => 
                        n.id === notification.id 
                            ? { ...n, isRead: true }
                            : n
                    )
                );

                // Call backend to mark as read
                try {
                    await apiService.markAlertAsReadForUser(notification.id, microsoftUserId);
                } catch (markError) {
                    console.error('Failed to auto-mark as read:', markError);
                }
                
                // Update the current notification object
                notification = { ...notification, isRead: true };
            }
            
            setCurrentNotification(notification);
            onViewOpen();
        } catch (error) {
            console.error('Failed to open notification modal:', error);
            // Still show the modal even if marking as read fails
            setCurrentNotification(notification);
            onViewOpen();
        }
    };

    /**
     * Export ALL notifications to Excel with multiple sheets
     */
    const handleExportNotifications = async () => {
        try {
            setExportLoading(true);

            // Fetch all notifications for export using backend API
            const allNotificationsResponse = await apiService.getAlertsWithUserReadStatus({
                page: 1,
                size: 50000, // Large number to get all
                sortBy: 'createdAtUtc',
                sortDirection: sortOrder.toUpperCase(),
                // Apply current filters to export
                clientId: clientFilter !== 'all' ? parseInt(clientFilter) : undefined,
                category: categoryFilter !== 'all' ? categoryFilter : undefined,
                status: statusFilter !== 'all' ? statusFilter : undefined,
                search: searchTerm.trim() || undefined,
                readStatus: readFilter !== 'all' ? readFilter : undefined
            });

            if (!allNotificationsResponse.success) {
                showErrorToast('Failed to fetch notifications for export');
                return;
            }

            const allNotificationsData = allNotificationsResponse.data?.content || allNotificationsResponse.data || [];

            if (allNotificationsData.length === 0) {
                showErrorToast('No notification data available for export');
                return;
            }

            const clientFilterText = clientFilter !== 'all' ? ` (filtered by client ${clientFilter})` : '';
            showSuccessToast(`Fetched ${allNotificationsData.length} notifications for export${clientFilterText}`);

            // Map notifications
            const mappedNotifications = allNotificationsData.map((notification, index) => ({
                ...notification,
                id: notification.alertId || notification.id || `alert_${index + 1}`,
                createdAt: notification.createdAtUtc || notification.createdAt || new Date().toISOString(),
                isRead: notification.isRead || false,
                category: notification.category || 'Unknown',
                status: notification.status || 'info',
                message: notification.message || 'No message available'
            }));

            // Create workbook and sheets
            const workbook = XLSX.utils.book_new();

            // Main notifications data
            const notificationsData = mappedNotifications.map((notification, index) => ({
                'Row': index + 1,
                'Notification ID': notification.id,
                'Alert ID': notification.alertId || notification.id || '',
                'Client ID': notification.clientId || '',
                'Category': notification.category || 'Unknown',
                'Status': notification.status || 'info',
                'Message': parseEscapeSequences(notification.message) || 'No message available',
                'Read Status': notification.isRead ? 'Read' : 'Unread',
                'Created Date (UTC)': notification.createdAt ? formatDateTime(notification.createdAt) : 'Unknown',
                'Time Ago': notification.createdAt ? getRelativeTime(notification.createdAt) : 'Unknown',
                'Is Read Flag': notification.isRead ? 'TRUE' : 'FALSE'
            }));

            const notificationsWS = XLSX.utils.json_to_sheet(notificationsData);
            const colWidths = [
                { wch: 8 }, { wch: 25 }, { wch: 20 }, { wch: 12 }, { wch: 15 }, 
                { wch: 10 }, { wch: 60 }, { wch: 12 }, { wch: 22 }, { wch: 15 }, { wch: 12 }
            ];
            notificationsWS['!cols'] = colWidths;
            XLSX.utils.book_append_sheet(workbook, notificationsWS, 'All Notifications');

            // Create unread notifications sheet
            const unreadNotifications = mappedNotifications.filter(notification => !notification.isRead);
            if (unreadNotifications.length > 0) {
                const unreadData = unreadNotifications.map((notification, index) => ({
                    'Row': index + 1,
                    'Notification ID': notification.id,
                    'Client ID': notification.clientId || '',
                    'Category': notification.category || 'Unknown',
                    'Status': notification.status || 'info',
                    'Message': parseEscapeSequences(notification.message) || 'No message available',
                    'Created Date (UTC)': notification.createdAt ? formatDateTime(notification.createdAt) : 'Unknown',
                    'Time Ago': notification.createdAt ? getRelativeTime(notification.createdAt) : 'Unknown'
                }));

                const unreadWS = XLSX.utils.json_to_sheet(unreadData);
                unreadWS['!cols'] = colWidths.slice(0, 8);
                XLSX.utils.book_append_sheet(workbook, unreadWS, 'Unread Notifications');
            }

            // Generate filename and download
            const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
            const clientSuffix = clientFilter !== 'all' ? `_client_${clientFilter}` : '';
            const filename = `notifications_export_${mappedNotifications.length}_records${clientSuffix}_${timestamp}.xlsx`;

            XLSX.writeFile(workbook, filename);
            usageTracker.action('User Notifications', 'EXPORT_EXCEL');
            showSuccessToast(`Export Complete! ${mappedNotifications.length} notifications exported to ${filename}`);

        } catch (error) {
            showErrorToast(`Export failed: ${error.message}. Please try again.`);
        } finally {
            setExportLoading(false);
        }
    };

    /**
     * Handle notification deletion
     */
    const handleDeleteNotification = async () => {
        try {
            if (!currentNotification?.id && !currentNotification?.alertId) return;

            const alertId = currentNotification.id || currentNotification.alertId;
            const response = await apiService.deleteAlert(alertId);
            
            if (response.success) {
                showSuccessToast('Notification deleted successfully');
                onDeleteClose();
                setCurrentNotification(null);
                loadNotifications();
            }
        } catch (error) {
            showErrorToast('Failed to delete notification');
        }
    };

    /**
     * Open delete confirmation modal
     */
    const openDeleteModal = (notification) => {
        setCurrentNotification(notification);
        onDeleteOpen();
    };

    /**
     * Reset all filters to default values
     */
    const handleResetFilters = () => {
        setSearchTerm('');
        setCategoryFilter('all');
        setStatusFilter('all');
        setReadFilter('all');
        setClientFilter('all');
        setSortOrder('desc');
        setCurrentPage(1);
        setSelectedNotifications([]);
        
        // Clear sessionStorage toast flags
        Object.keys(sessionStorage).forEach(key => {
            if (key.startsWith('notification_toast_')) {
                sessionStorage.removeItem(key);
            }
        });
        
        loadAvailableClients();
        showSuccessToast('All filters reset successfully');
    };

    /**
     * Check if any filters are active
     */
    const hasActiveFilters = () => {
        return searchTerm !== '' || 
               categoryFilter !== 'all' || 
               statusFilter !== 'all' || 
               readFilter !== 'all' ||
               clientFilter !== 'all' || 
               sortOrder !== 'desc';
    };

    /**
     * Generate page numbers for pagination display
     */
    const getPageNumbers = () => {
        const totalPages = Math.ceil(totalResults / pageSize);
        const maxVisiblePages = 10;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
        
        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }
        
        const pages = [];
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        
        return pages;
    };

    /**
     * Get notification icon based on category and status
     */
    const getNotificationIcon = (category, status) => {
        if (status === 'error') return MdError;
        if (status === 'warning') return MdWarning;
        if (status === 'success') return MdCheckCircle;
        if (status === 'issue') return MdError;
        if(status === 'critical') return MdError;
        if(status ==='info') return MdInfo;
        switch (category?.toLowerCase()) {
            case 'heart beat':
            case 'heartbeat':
                return MdError;
            case 'verification':
                return MdCheckCircle;
            case 'system':
                return MdInfo;
            default:
                return MdNotifications;
        }
    };

    /**
     * Get notification color based on status
     */
    const getNotificationColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'error':
                return 'red';
            case 'warning':
                return 'orange';
            case 'success':
                return 'green';
            case 'issue':
                return 'red';
            case 'critical':
                return 'red';
            case 'info':
            default:
                return 'blue';
        }
    };

    /**
     * Show success toast notification
     */
    const showSuccessToast = (message) => {
        toast({
            title: message,
            status: 'success',
            duration: 3000,
            isClosable: true,
            position: 'top-right'
        });
    };

    /**
     * Show error toast notification
     */
    const showErrorToast = (message) => {
        toast({
            title: message,
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top-right'
        });
    };

    const totalPages = Math.ceil(totalResults / pageSize);

    return (
        <Box>
            <VStack spacing={6} align="stretch">
                {/* Page Header */}
                <Flex justify="space-between" align="center">
                    <Box>
                        <Heading size="lg" color={headingColor}>
                            Notifications
                        </Heading>
                        <Text color={textColor} mt={1}>
                            Report triggered alerts and notification management
                        </Text>
                        {lastUpdated && (
                            <Text fontSize="sm" color={textColor} mt={1}>
                                Last updated: {formatDateTime(lastUpdated)}
                            </Text>
                        )}
                        {clientFilter !== 'all' && (
                            <Badge colorScheme="blue" variant="solid" mt={2}>
                                Filtered by Client: {availableClients.find(c => c.id.toString() === clientFilter)?.name || clientFilter}
                            </Badge>
                        )}
                        <HStack spacing={4} mt={2}>
                            <Badge colorScheme="green" variant="outline" fontSize="xs">
                                {availableClients.length} Clients Available
                            </Badge>
                            <Badge colorScheme="purple" variant="outline" fontSize="xs">
                                {availableCategories.length} Categories Available
                            </Badge>
                        </HStack>
                    </Box>
                    <HStack spacing={3}>
                        <HStack>
                            <Text fontSize="sm">Auto-refresh:</Text>
                            <Switch
                                isChecked={autoRefresh}
                                onChange={(e) => setAutoRefresh(e.target.checked)}
                                size="sm"
                            />
                        </HStack>
                        <Button
                            leftIcon={<MdRefresh />}
                            onClick={loadNotifications}
                            variant="outline"
                            size="sm"
                        >
                            Refresh
                        </Button>
                        <Tooltip label="Export ALL filtered notifications to Excel (.xlsx)" hasArrow placement="bottom">
                            <Button
                                leftIcon={<MdFileDownload />}
                                onClick={handleExportNotifications}
                                variant="outline"
                                size="sm"
                                colorScheme="green"
                                isLoading={exportLoading}
                                loadingText="Exporting..."
                                isDisabled={totalResults === 0}
                            >
                                Export All
                            </Button>
                        </Tooltip>
                    </HStack>
                </Flex>

                {/* Enhanced Filters and Search */}
                <Card bg={cardBg} borderColor={borderColor}>
                    <CardHeader>
                        <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                            <HStack spacing={2}>
                                <MdSearch />
                                <Heading size="md">Filters & Search</Heading>
                                {hasActiveFilters() && (
                                    <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                                        Active Filters
                                    </Badge>
                                )}
                            </HStack>
                            <Button
                                size="sm"
                                variant="ghost"
                                colorScheme="gray"
                                leftIcon={<MdRefresh />}
                                onClick={handleResetFilters}
                                isDisabled={!hasActiveFilters()}
                            >
                                Reset All Filters
                            </Button>
                        </Flex>
                    </CardHeader>
                    <CardBody>
                        <VStack spacing={4} align="stretch">
                            {/* Search Section */}
                            <Box>
                                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>
                                    Search
                                </Text>
                                <InputGroup maxW="400px">
                                    <InputLeftElement>
                                        <MdSearch color={textColor} />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Search notifications by message or category..."
                                        value={searchTerm}
                                        onChange={(e) => handleSearchChange(e.target.value)}
                                        bg={inputBg}
                                        borderColor={inputBorder}
                                        color={inputText}
                                        _focus={{
                                            borderColor: inputFocusBorder,
                                            boxShadow: `0 0 0 1px ${inputFocusBorder}`
                                        }}
                                    />
                                </InputGroup>
                            </Box>

                            {/* Filters Section */}
                            <Box>
                                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={3}>
                                    Filters
                                </Text>
                                <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={4} w="full">
                                    {/* Client Filter */}
                                    <Box>
                                        <Text fontSize="xs" color={textColor} mb={2} fontWeight="medium">
                                            Client
                                            {clientFilter !== 'all' && (
                                                <Badge ml={2} colorScheme="blue" variant="outline" fontSize="2xs">
                                                    Filtered
                                                </Badge>
                                            )}
                                        </Text>
                                        <Select
                                            value={clientFilter}
                                            onChange={(e) => handleFilterChange('client', e.target.value)}
                                            bg={inputBg}
                                            borderColor={clientFilter !== 'all' ? 'brand.300' : inputBorder}
                                            color={inputText}
                                            isDisabled={clientsLoading}
                                        >
                                            <option value="all">
                                                {clientsLoading ? 'Loading clients...' : 
                                                 availableClients.length === 0 ? 'No clients available' : 'All Clients'}
                                            </option>
                                            {!clientsLoading && availableClients.length > 0 && availableClients.map((client) => (
                                                <option key={client.id} value={client.id.toString()}>
                                                    {client.name}
                                                </option>
                                            ))}
                                        </Select>
                                    </Box>

                                    {/* Category Filter */}
                                    <Box>
                                        <Text fontSize="xs" color={textColor} mb={2} fontWeight="medium">
                                            Category
                                            {categoryFilter !== 'all' && (
                                                <Badge ml={2} colorScheme="blue" variant="outline" fontSize="2xs">
                                                    Filtered
                                                </Badge>
                                            )}
                                        </Text>
                                        <Select
                                            value={categoryFilter}
                                            onChange={(e) => handleFilterChange('category', e.target.value)}
                                            bg={inputBg}
                                            borderColor={categoryFilter !== 'all' ? 'brand.300' : inputBorder}
                                            color={inputText}
                                        >
                                            <option value="all">
                                                {availableCategories.length === 0 ? 'No categories available' : 'All Categories'}
                                            </option>
                                            {availableCategories.length > 0 && availableCategories.map((category) => (
                                                <option key={category} value={category}>
                                                    {category}
                                                </option>
                                            ))}
                                        </Select>
                                    </Box>

                                    {/* Status Filter */}
                                    <Box>
                                        <Text fontSize="xs" color={textColor} mb={2} fontWeight="medium">
                                            Status
                                            {statusFilter !== 'all' && (
                                                <Badge ml={2} colorScheme="blue" variant="outline" fontSize="2xs">
                                                    Filtered
                                                </Badge>
                                            )}
                                        </Text>
                                        <Select
                                            value={statusFilter}
                                            onChange={(e) => handleFilterChange('status', e.target.value)}
                                            bg={inputBg}
                                            borderColor={statusFilter !== 'all' ? 'brand.300' : inputBorder}
                                            color={inputText}
                                        >
                                            <option value="all">All Status</option>
                                            <option value="error">❌ Error</option>
                                            <option value="warning">⚠️ Warning</option>
                                            <option value="success">✅ Success</option>
                                            <option value="info">ℹ️ Info</option>
                                        </Select>
                                    </Box>

                                    {/* Read Status Filter */}
                                    <Box>
                                        <Text fontSize="xs" color={textColor} mb={2} fontWeight="medium">
                                            Read Status
                                            {readFilter !== 'all' && (
                                                <Badge ml={2} colorScheme="blue" variant="outline" fontSize="2xs">
                                                    Filtered
                                                </Badge>
                                            )}
                                        </Text>
                                        <Select
                                            value={readFilter}
                                            onChange={(e) => handleFilterChange('read', e.target.value)}
                                            bg={inputBg}
                                            borderColor={readFilter !== 'all' ? 'brand.300' : inputBorder}
                                            color={inputText}
                                        >
                                            <option value="all">All</option>
                                            <option value="unread">📬 Unread</option>
                                            <option value="read">📭 Read</option>
                                        </Select>
                                    </Box>

                                    {/* Sort Order */}
                                    <Box>
                                        <Text fontSize="xs" color={textColor} mb={2} fontWeight="medium">
                                            Sort Order
                                        </Text>
                                        <Select
                                            value={sortOrder}
                                            onChange={(e) => handleSortChange(e.target.value)}
                                            bg={inputBg}
                                            color={inputText}
                                        >
                                            <option value="desc">🔽 Newest First</option>
                                            <option value="asc">🔼 Oldest First</option>
                                        </Select>
                                    </Box>
                                </SimpleGrid>
                            </Box>
                        </VStack>
                    </CardBody>
                </Card>

                {/* Notifications Table */}
                <Card bg={cardBg} borderColor={borderColor}>
                    <CardHeader>
                        <HStack justify="space-between">
                            <Heading size="md">
                                Notifications ({formatNumber(totalResults)} total)
                            </Heading>
                            <HStack spacing={4}>
                                <Text fontSize="sm" color={textColor}>
                                    Page {currentPage} of {totalPages}
                                </Text>
                                <Badge colorScheme="red" variant="subtle">
                                    Unread: {formatNumber(filteredNotifications.filter(n => !n.isRead).length)}
                                </Badge>
                                {autoRefresh && (
                                    <Badge colorScheme="green" variant="subtle">
                                        Auto-refresh ON
                                    </Badge>
                                )}
                            </HStack>
                        </HStack>
                    </CardHeader>
                    <CardBody p={0}>
                        {loading ? (
                            <Flex justify="center" p={8}>
                                <Spinner size="lg" color="brand.500" />
                            </Flex>
                        ) : (
                            <>
                                <TableContainer>
                                    <Table variant="simple" size="sm">
                                        <Thead>
                                            <Tr>
                                                <Th w="50px">Status</Th>
                                                <Th>Category</Th>
                                                <Th>Message</Th>
                                                <Th>Date</Th>
                                                <Th>Time Ago</Th>
                                                <Th w="100px">Actions</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {filteredNotifications.map((notification) => {
                                                const NotificationIcon = getNotificationIcon(notification.category, notification.status);
                                                const isUnread = !notification.isRead;
                                                
                                                return (
                                                    <Tr 
                                                        key={notification.id}
                                                        bg={isUnread ? unreadBg : 'transparent'}
                                                        _hover={{ bg: bgColor }}
                                                        cursor="pointer"
                                                        onClick={() => openViewModal(notification)}
                                                    >
                                                        <Td>
                                                            <HStack spacing={2}>
                                                                <Icon
                                                                    as={NotificationIcon}
                                                                    color={`${getNotificationColor(notification.status)}.500`}
                                                                    w={4}
                                                                    h={4}
                                                                />
                                                                {isUnread && (
                                                                    <Badge
                                                                        colorScheme="blue"
                                                                        variant="solid"
                                                                        fontSize="xs"
                                                                        borderRadius="full"
                                                                        w={2}
                                                                        h={2}
                                                                        p={0}
                                                                    />
                                                                )}
                                                            </HStack>
                                                        </Td>
                                                        <Td>
                                                            <Badge
                                                                colorScheme={getNotificationColor(notification.status)}
                                                                variant="subtle"
                                                            >
                                                                {notification.category}
                                                            </Badge>
                                                        </Td>
                                                        <Td>
                                                            <Text
                                                                noOfLines={2}
                                                                maxW="400px"
                                                                fontSize="sm"
                                                                fontWeight={isUnread ? 'medium' : 'normal'}
                                                                title={parseEscapeSequences(notification.message)}
                                                            >
                                                                {parseEscapeSequences(notification.message)}
                                                            </Text>
                                                        </Td>
                                                        <Td fontSize="sm" color={textColor}>
                                                            {formatDateTime(notification.createdAt)}
                                                        </Td>
                                                        <Td fontSize="sm" color={textColor}>
                                                            {getRelativeTime(notification.createdAt)}
                                                        </Td>
                                                        <Td onClick={(e) => e.stopPropagation()}>
                                                            <Menu>
                                                                <MenuButton
                                                                    as={IconButton}
                                                                    icon={<MdMoreVert />}
                                                                    variant="ghost"
                                                                    size="sm"
                                                                />
                                                                <MenuList>
                                                                    <MenuItem
                                                                        icon={isUnread ? <MdMarkEmailRead /> : <MdMarkEmailUnread />}
                                                                        onClick={() => toggleNotificationRead(notification.id, notification.isRead)}
                                                                    >
                                                                        Mark as {isUnread ? 'Read' : 'Unread'}
                                                                    </MenuItem>
                                                                    <MenuItem
                                                                        icon={<MdDelete />}
                                                                        onClick={() => openDeleteModal(notification)}
                                                                        color="red.500"
                                                                    >
                                                                        Delete
                                                                    </MenuItem>
                                                                </MenuList>
                                                            </Menu>
                                                        </Td>
                                                    </Tr>
                                                );
                                            })}
                                        </Tbody>
                                    </Table>
                                </TableContainer>

                                {/* Enhanced Pagination Controls */}
                                {totalPages > 1 && (
                                    <Flex justify="space-between" align="center" p={4} borderTop="1px" borderTopColor={borderColor}>
                                        <Text fontSize="sm" color={textColor}>
                                            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalResults)} of {formatNumber(totalResults)} entries
                                        </Text>
                                        
                                        <HStack spacing={2}>
                                            <Button
                                                size="sm"
                                                onClick={() => handlePageChange(1)}
                                                isDisabled={currentPage === 1}
                                                variant="outline"
                                            >
                                                First
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                isDisabled={currentPage === 1}
                                                variant="outline"
                                            >
                                                Previous
                                            </Button>
                                            
                                            {getPageNumbers().map(page => (
                                                <Button
                                                    key={page}
                                                    size="sm"
                                                    variant={currentPage === page ? "solid" : "outline"}
                                                    colorScheme={currentPage === page ? "brand" : "gray"}
                                                    onClick={() => handlePageChange(page)}
                                                >
                                                    {page}
                                                </Button>
                                            ))}
                                            
                                            <Button
                                                size="sm"
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                isDisabled={currentPage === totalPages}
                                                variant="outline"
                                            >
                                                Next
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={() => handlePageChange(totalPages)}
                                                isDisabled={currentPage === totalPages}
                                                variant="outline"
                                            >
                                                Last
                                            </Button>
                                        </HStack>
                                    </Flex>
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>

                {/* View Notification Modal */}
                <Modal isOpen={isViewOpen} onClose={onViewClose} size="lg">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Notification Details</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            {currentNotification && (
                                <VStack spacing={4} align="stretch">
                                    <HStack spacing={4}>
                                        <Icon
                                            as={getNotificationIcon(currentNotification.category, currentNotification.status)}
                                            color={`${getNotificationColor(currentNotification.status)}.500`}
                                            w={6}
                                            h={6}
                                        />
                                        <VStack align="start" spacing={1}>
                                            <Badge
                                                colorScheme={getNotificationColor(currentNotification.status)}
                                                variant="subtle"
                                            >
                                                {currentNotification.category} - {currentNotification.status}
                                            </Badge>
                                            <Text fontSize="sm" color={textColor}>
                                                {formatDateTime(currentNotification.createdAt)}
                                            </Text>
                                        </VStack>
                                    </HStack>
                                    
                                    <Box>
                                        <Text fontWeight="bold" mb={2}>Message:</Text>
                                        <Text fontSize="sm" lineHeight="tall" whiteSpace="pre-wrap">
                                            {formatMessageDisplay(currentNotification.message)}
                                        </Text>
                                    </Box>
                                    
                                    <HStack spacing={4} fontSize="sm" color={textColor}>
                                        <Text>
                                            <strong>Created:</strong> {getRelativeTime(currentNotification.createdAt)}
                                        </Text>
                                        <Text>
                                            <strong>Status:</strong> {currentNotification.isRead ? 'Read' : 'Unread'}
                                        </Text>
                                        {currentNotification.clientId && (
                                            <Text>
                                                <strong>Client ID:</strong> {currentNotification.clientId}
                                            </Text>
                                        )}
                                    </HStack>
                                </VStack>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onViewClose}>
                                Close
                            </Button>
                            {currentNotification && (
                                <Button
                                    colorScheme="red"
                                    onClick={() => {
                                        onViewClose();
                                        openDeleteModal(currentNotification);
                                    }}
                                >
                                    Delete
                                </Button>
                            )}
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Confirm Deletion</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <Alert status="warning" borderRadius="md">
                                <AlertIcon />
                                Are you sure you want to delete this notification? This action cannot be undone.
                            </Alert>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onDeleteClose}>
                                Cancel
                            </Button>
                            <Button colorScheme="red" onClick={handleDeleteNotification}>
                                Delete Notification
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            </VStack>
        </Box>
    );
}

export default UserNotifications;